import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Wallet, ChevronLeft, ChevronRight, Loader2, Edit3, X,
  CheckCircle2, AlertCircle, Clock, Banknote, Coins, History,
} from 'lucide-react';

// Helpers
const monthLabel = (m) => {
  const [y, mo] = m.split('-');
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleString('en-GB', { month: 'long', year: 'numeric' });
};
const adjustMonth = (m, delta) => {
  const [y, mo] = m.split('-').map(Number);
  const dt = new Date(y, mo - 1 + delta, 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
};
const todayMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const todayDate = () => new Date().toISOString().slice(0, 10);

export default function PayrollPage() {
  const { api } = useAuth();
  const [month, setMonth] = useState(todayMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null); // employee_id when drawer open
  const [rateModal, setRateModal] = useState(null); // employee row when rate edit
  const [cashRow, setCashRow] = useState(null); // employee_id when inline cash form
  const [adjustModal, setAdjustModal] = useState(null); // employee row when adjustment edit
  const [showInactive, setShowInactive] = useState(false);
  const [includeTipsInTotal, setIncludeTipsInTotal] = useState(false);
  const [diagnostic, setDiagnostic] = useState(null);

  const fetchDiagnostic = useCallback(async () => {
    try {
      const res = await api.get('/payroll/diagnostic/unmatched-labor');
      setDiagnostic(res.data);
    } catch { /* ignore */ }
  }, [api]);

  const fetchMonth = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/payroll/monthly?month=${month}`);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load payroll');
    } finally { setLoading(false); }
  }, [api, month]);

  useEffect(() => { fetchMonth(); fetchDiagnostic(); }, [fetchMonth, fetchDiagnostic]);

  const submitRateHistory = async (employee_id, history) => {
    try {
      await api.put(`/payroll/employees/${employee_id}/rate-history`, { history });
      toast.success('Rate history saved. Earnings recalculated.');
      setRateModal(null);
      fetchMonth();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save rate history');
    }
  };

  const submitCashPayment = async (employee_id, amount, payment_date, notes) => {
    try {
      await api.post(`/payroll/employees/${employee_id}/payment`, {
        amount: parseFloat(amount), payment_date, payment_method: 'cash', notes,
      });
      toast.success('Cash payment recorded');
      setCashRow(null);
      fetchMonth();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const submitAdjustment = async (employee_id, amount, date, reason) => {
    try {
      await api.post(`/payroll/employees/${employee_id}/adjustment`, {
        amount: parseFloat(amount), date, reason,
      });
      toast.success(`Balance ${parseFloat(amount) >= 0 ? 'increased' : 'reduced'} by £${Math.abs(parseFloat(amount)).toFixed(2)}`);
      setAdjustModal(null);
      fetchMonth();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const runMigration = async () => {
    try {
      const res = await api.post('/payroll/migrate-time-entries');
      const { files_processed, days_upserted } = res.data || {};
      toast.success(`Rebuilt ${days_upserted || 0} day${days_upserted === 1 ? '' : 's'} from ${files_processed || 0} payroll upload${files_processed === 1 ? '' : 's'}.`);
      fetchMonth();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Migration failed');
    }
  };

  if (loading && !data) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="payroll-page">
      {/* Header with month switcher */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wallet className="w-6 h-6" /> Payroll
          </h1>
          <p className="text-sm text-muted-foreground">Hours from your POS · rate history · bank &amp; cash payments — joined per employee per month.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={runMigration} title="Rebuild per-day time entries from all stored payroll CSV uploads" data-testid="migrate-btn">
            <History className="w-4 h-4 mr-1" /> Rebuild
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setMonth(adjustMonth(month, -1))} data-testid="month-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="font-semibold text-base px-3" data-testid="month-label">{monthLabel(month)}</div>
          <Button size="sm" variant="ghost" onClick={() => setMonth(adjustMonth(month, 1))} data-testid="month-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => setMonth(todayMonth())} data-testid="month-today">Today</Button>
        </div>
      </div>

      {/* Diagnostic: unmatched Toast records */}
      {diagnostic && diagnostic.unmatched_count > 0 && (
        <Card className="border-amber-300 bg-amber-50/50" data-testid="unmatched-banner">
          <CardContent className="py-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="font-medium text-amber-900">
                  {diagnostic.unmatched_count} POS labour record{diagnostic.unmatched_count === 1 ? '' : 's'} not matched to any employee
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  These hours are sitting in the system but won&apos;t appear on any employee&apos;s ledger until matched. Common causes: name mismatch, employee not yet added, or typo in your POS.
                </p>
                <div className="mt-2 space-y-1">
                  {diagnostic.unmatched_groups.slice(0, 5).map((g, i) => (
                    <div key={i} className="text-xs flex items-center justify-between bg-white rounded p-1.5">
                      <span className="font-mono">{g.name}</span>
                      <span className="text-muted-foreground">{g.count} period{g.count === 1 ? '' : 's'} · £{g.total_amount.toFixed(2)}</span>
                    </div>
                  ))}
                  {diagnostic.unmatched_groups.length > 5 && (
                    <p className="text-xs text-muted-foreground">+ {diagnostic.unmatched_groups.length - 5} more…</p>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  💡 Add a matching employee under <strong>Staff</strong>, or set a POS alias on an existing employee to bridge name differences.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Totals */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="totals-row">
          <StatCard label="Hours" value={data.totals.hours.toFixed(1)} sub={`${data.totals.employee_count} employees`} />
          <StatCard label="Earned (this month)" value={`£${data.totals.earned.toLocaleString('en-GB', { minimumFractionDigits: 2 })}`} color="text-blue-600" />
          <StatCard label="Bank paid (this month)" value={`£${data.totals.bank_paid.toLocaleString('en-GB', { minimumFractionDigits: 2 })}`} color="text-green-600" />
          <StatCard label="Cash paid (this month)" value={`£${data.totals.cash_paid.toLocaleString('en-GB', { minimumFractionDigits: 2 })}`} color="text-amber-600" />
          <StatCard
            label={data.totals.balance_owed >= 0 ? 'Owed to staff (rolling)' : 'Overpaid (rolling)'}
            value={`£${Math.abs(data.totals.balance_owed).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`}
            color={data.totals.balance_owed >= 0 ? 'text-red-600' : 'text-emerald-600'}
            sub={data.totals.opening_balance !== 0 ? `£${data.totals.opening_balance.toFixed(2)} carried in` : null}
          />
        </div>
      )}

      {/* Tip split banner */}
      {data?.tip_split && data.tip_split.mode !== 'by_logged_user' && (
        <div className="rounded-md border border-purple-200 bg-purple-50 px-4 py-2 text-xs text-purple-900 flex items-center justify-between" data-testid="tip-split-banner">
          <div>
            <strong>Tips pooled & split by {data.tip_split.mode === 'pool_by_hours' ? 'hours' : 'orders'}</strong>
            {' '} — pool £{(data.tip_split.pool_total || 0).toFixed(2)} ÷ {data.tip_split.mode === 'pool_by_hours' ? `${(data.tip_split.pool_total_hours || 0).toFixed(1)}h` : `${data.tip_split.pool_total_orders || 0} orders`}
            {' '} across {data.tip_split.eligible_count} eligible employee{data.tip_split.eligible_count === 1 ? '' : 's'}.
          </div>
          <a href="/settings" className="underline hover:text-purple-700">Change in Settings →</a>
        </div>
      )}

      {/* Grid */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Employees</CardTitle>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="include-tips-toggle">
              <input
                type="checkbox"
                checked={includeTipsInTotal}
                onChange={(e) => setIncludeTipsInTotal(e.target.checked)}
                className="cursor-pointer"
              />
              Include tips in total
            </label>
            {(() => {
              const inactive = (data?.employees || []).filter((e) => (e.total_hours || 0) === 0 && (e.month_net || 0) === 0).length;
              if (inactive === 0) return null;
              return (
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="inactive-toggle">
                  <input
                    type="checkbox"
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    className="cursor-pointer"
                  />
                  Show inactive ({inactive} hidden)
                </label>
              );
            })()}
          </div>
        </CardHeader>
        <CardContent>
          {(() => {
            const visibleEmps = (data?.employees || []).filter(
              (e) => showInactive || (e.total_hours || 0) > 0 || (e.month_net || 0) !== 0
            );
            if (visibleEmps.length === 0) {
              return <p className="text-sm text-muted-foreground py-6 text-center" data-testid="empty-employees">No employees with hours or activity this month.</p>;
            }
            return (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground border-b">
                  <tr>
                    <th className="text-left font-medium py-2 px-2">Employee</th>
                    <th className="text-right font-medium py-2 px-2">Rate</th>
                    <th className="text-right font-medium py-2 px-2">Hours</th>
                    <th className="text-right font-medium py-2 px-2">Earned</th>
                    <th className="text-right font-medium py-2 px-2">Tips</th>
                    <th className="text-right font-medium py-2 px-2">Balance</th>
                    <th className="text-right font-medium py-2 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleEmps.map(e => {
                    const tips = e.total_tips || 0;
                    const displayBalance = includeTipsInTotal ? e.balance_owed + tips : e.balance_owed;
                    return (
                    <React.Fragment key={e.employee_id}>
                      <tr
                        className="border-b hover:bg-muted/40 transition-colors cursor-pointer"
                        onClick={() => setDrawer(e.employee_id)}
                        data-testid={`row-${e.employee_id}`}
                      >
                        <td className="py-2 px-2">
                          <div className="font-medium flex items-center gap-1 flex-wrap">
                            {e.employee_name}
                            {e.day_count > 0 && (
                              <span className="text-[10px] text-muted-foreground" data-testid={`days-${e.employee_id}`}>
                                {e.day_count} day{e.day_count === 1 ? '' : 's'}
                              </span>
                            )}
                          </div>
                          {e.role && <div className="text-xs text-muted-foreground">{e.role}</div>}
                        </td>
                        <td className="text-right py-2 px-2 font-mono">
                          <button
                            className="hover:text-primary inline-flex items-center gap-1"
                            onClick={(ev) => { ev.stopPropagation(); setRateModal(e); }}
                            data-testid={`rate-${e.employee_id}`}
                          >
                            £{(e.current_rate || 0).toFixed(2)} <Edit3 className="w-3 h-3 opacity-50" />
                          </button>
                        </td>
                        <td className="text-right py-2 px-2 font-mono">{e.total_hours.toFixed(1)}</td>
                        <td className="text-right py-2 px-2 font-mono text-blue-600">£{e.total_earned.toFixed(2)}</td>
                        <td className="text-right py-2 px-2 font-mono text-purple-600" data-testid={`tips-${e.employee_id}`}>
                          {(() => {
                            const b = e.tip_share_basis || {};
                            if (b.basis === 'hours' || b.basis === 'orders') {
                              const unit = b.basis === 'hours' ? 'h' : 'orders';
                              const myVal = b.basis === 'hours' ? (b.my_value || 0).toFixed(1) : (b.my_value || 0);
                              const divVal = b.basis === 'hours' ? (b.pool_divisor || 0).toFixed(1) : (b.pool_divisor || 0);
                              const title = b.excluded_reason
                                ? `Excluded from pool: ${b.excluded_reason}\nPool: £${(b.pool_total||0).toFixed(2)} ÷ ${divVal} ${unit}`
                                : `Pool £${(b.pool_total||0).toFixed(2)} ÷ ${divVal} ${unit} × your ${myVal} ${unit} = £${tips.toFixed(2)}\nAttributed by POS: £${(e.attributed_tips || 0).toFixed(2)}`;
                              return (
                                <span title={title} className="inline-flex items-center gap-1 cursor-help">
                                  £{tips.toFixed(2)}
                                  <span className="text-[10px] text-muted-foreground">ⓘ</span>
                                </span>
                              );
                            }
                            return `£${tips.toFixed(2)}`;
                          })()}
                        </td>
                        <td className={`text-right py-2 px-2 font-mono font-semibold ${displayBalance > 0 ? 'text-red-600' : displayBalance < 0 ? 'text-emerald-600' : ''}`} data-testid={`balance-${e.employee_id}`}>
                          {displayBalance > 0 ? '+' : displayBalance < 0 ? '−' : ''}£{Math.abs(displayBalance).toFixed(2)}
                        </td>
                        <td className="text-right py-2 px-2">
                          <Button size="sm" variant="ghost" className="h-7"
                            onClick={(ev) => { ev.stopPropagation(); setCashRow(e.employee_id === cashRow ? null : e.employee_id); }}
                            data-testid={`cash-${e.employee_id}`}>
                            <Coins className="w-3 h-3 mr-1" /> Cash
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7"
                            onClick={(ev) => { ev.stopPropagation(); setAdjustModal(e); }}
                            data-testid={`adjust-${e.employee_id}`}>
                            <Edit3 className="w-3 h-3 mr-1" /> Adjust
                          </Button>
                        </td>
                      </tr>
                      {cashRow === e.employee_id && (
                        <CashPaymentRow
                          employee_id={e.employee_id}
                          balance={e.balance_owed}
                          onCancel={() => setCashRow(null)}
                          onSubmit={submitCashPayment}
                        />
                      )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            );
          })()}
        </CardContent>
      </Card>

      {drawer && (
        <EmployeeDetailDrawer
          employee_id={drawer}
          onClose={() => setDrawer(null)}
          onChanged={fetchMonth}
        />
      )}

      {rateModal && (
        <RateHistoryModal
          employee={rateModal}
          api={api}
          onClose={() => setRateModal(null)}
          onSave={submitRateHistory}
        />
      )}

      {adjustModal && (
        <AdjustmentModal
          employee={adjustModal}
          onClose={() => setAdjustModal(null)}
          onSave={submitAdjustment}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, sub, color = '' }) {
  return (
    <Card><CardContent className="py-3 text-center">
      <p className={`text-lg font-bold ${color}`}>{value}</p>
      <p className="text-xs text-muted-foreground">{label}{sub ? ` · ${sub}` : ''}</p>
    </CardContent></Card>
  );
}

function CashPaymentRow({ employee_id, balance, onCancel, onSubmit }) {
  const [amount, setAmount] = useState(balance > 0 ? balance.toFixed(2) : '');
  const [date, setDate] = useState(todayDate());
  const [notes, setNotes] = useState('');
  return (
    <tr className="bg-amber-50/50">
      <td colSpan={8} className="py-3 px-3">
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Amount £</Label>
            <Input className="h-8 w-28" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid={`cash-amount-${employee_id}`} />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Date</Label>
            <Input className="h-8 w-36" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid={`cash-date-${employee_id}`} />
          </div>
          <div className="flex-1 min-w-[200px]">
            <Label className="text-[10px] uppercase text-muted-foreground">Note</Label>
            <Input className="h-8" placeholder="e.g. Cash for Feb shifts" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`cash-note-${employee_id}`} />
          </div>
          <Button size="sm" onClick={() => onSubmit(employee_id, amount, date, notes)} disabled={!amount || parseFloat(amount) <= 0} data-testid={`cash-save-${employee_id}`}>
            <CheckCircle2 className="w-4 h-4 mr-1" /> Save
          </Button>
          <Button size="sm" variant="ghost" onClick={onCancel}><X className="w-4 h-4" /></Button>
        </div>
      </td>
    </tr>
  );
}

function RateHistoryModal({ employee, api, onClose, onSave }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.get(`/payroll/employees/${employee.employee_id}/rate-history`);
        if (!active) return;
        const list = (res.data?.history || []).map((h) => ({
          amount: String(h.amount ?? ''),
          effective_from: h.effective_from || '',
          note: h.note || '',
        }));
        if (list.length === 0) {
          list.push({
            amount: String(employee.current_rate || ''),
            effective_from: todayDate(),
            note: '',
          });
        }
        list.sort((a, b) => (a.effective_from || '').localeCompare(b.effective_from || ''));
        setHistory(list);
      } catch {
        toast.error('Failed to load rate history');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [api, employee]);

  const updateRow = (idx, key, value) => {
    setHistory((prev) => prev.map((row, i) => (i === idx ? { ...row, [key]: value } : row)));
  };
  const removeRow = (idx) => {
    setHistory((prev) => prev.filter((_, i) => i !== idx));
  };
  const addRow = () => {
    setHistory((prev) => [
      ...prev,
      { amount: String(employee.current_rate || ''), effective_from: todayDate(), note: '' },
    ]);
  };

  const sorted = [...history].sort((a, b) => (a.effective_from || '').localeCompare(b.effective_from || ''));
  const valid = sorted.length > 0
    && sorted.every((h) => h.effective_from && parseFloat(h.amount) > 0)
    && new Set(sorted.map((h) => h.effective_from)).size === sorted.length;

  const save = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      await onSave(
        employee.employee_id,
        sorted.map((h) => ({
          amount: parseFloat(h.amount),
          effective_from: h.effective_from,
          note: h.note,
        })),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="rate-history-modal">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold flex items-center gap-2">
            <History className="w-4 h-4" /> Rate history — {employee.employee_name}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Backdating works retroactively</p>
              <p className="text-blue-800 mt-1">
                Each entry sets the rate from <code>Effective from</code> until the next entry&apos;s date.
                E.g. add <code>£10.00 from 2025-01-01</code> and <code>£11.00 from 2025-04-01</code> — POS hours
                worked in Jan-Mar will earn at £10, April onwards at £11. Earnings recalculate immediately.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : (
            <div className="space-y-2">
              {sorted.map((row, idx) => {
                const realIdx = history.indexOf(row);
                const nextDate = sorted[idx + 1]?.effective_from;
                const isCurrent = !nextDate;
                return (
                  <div key={idx} className={`border rounded p-3 ${isCurrent ? 'border-emerald-300 bg-emerald-50/30' : 'border-border'}`} data-testid={`rate-row-${idx}`}>
                    <div className="flex items-end gap-2 flex-wrap">
                      <div className="flex-1 min-w-[100px]">
                        <Label className="text-[10px] uppercase text-muted-foreground">Rate (£/h)</Label>
                        <Input
                          type="number" step="0.01" min="0"
                          value={row.amount}
                          onChange={(e) => updateRow(realIdx, 'amount', e.target.value)}
                          className="h-8"
                          data-testid={`rate-row-amount-${idx}`}
                        />
                      </div>
                      <div className="flex-1 min-w-[140px]">
                        <Label className="text-[10px] uppercase text-muted-foreground">Effective from</Label>
                        <Input
                          type="date"
                          value={row.effective_from}
                          onChange={(e) => updateRow(realIdx, 'effective_from', e.target.value)}
                          className="h-8"
                          data-testid={`rate-row-from-${idx}`}
                        />
                      </div>
                      <div className="flex-[2] min-w-[160px]">
                        <Label className="text-[10px] uppercase text-muted-foreground">Note (optional)</Label>
                        <Input
                          value={row.note}
                          onChange={(e) => updateRow(realIdx, 'note', e.target.value)}
                          placeholder="e.g. April pay rise"
                          className="h-8"
                          data-testid={`rate-row-note-${idx}`}
                        />
                      </div>
                      <Button
                        size="sm" variant="ghost" className="h-8 text-red-600"
                        onClick={() => removeRow(realIdx)}
                        disabled={history.length === 1}
                        data-testid={`rate-row-remove-${idx}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1">
                      {isCurrent ? (
                        <span className="text-emerald-700 font-medium">Current rate (open-ended)</span>
                      ) : (
                        <>Until {nextDate}</>
                      )}
                    </div>
                  </div>
                );
              })}
              <Button size="sm" variant="outline" onClick={addRow} className="w-full" data-testid="rate-row-add">
                + Add rate period
              </Button>
              {!valid && history.length > 0 && (
                <p className="text-xs text-amber-600">
                  Each entry needs a positive amount and a unique effective_from date.
                </p>
              )}
            </div>
          )}
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!valid || saving} data-testid="rate-history-save">
            {saving && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
            Save rate history
          </Button>
        </div>
      </div>
    </div>
  );
}

function AdjustmentModal({ employee, onClose, onSave }) {
  // Modes:
  //   'target'   — user enters the desired NEW balance; we compute the adjustment
  //   'amount'   — user enters an adjustment amount with +/- direction
  const [mode, setMode] = useState('target');
  const [targetBalance, setTargetBalance] = useState('');
  const [amount, setAmount] = useState('');
  const [direction, setDirection] = useState('payment'); // 'earnings' = +, 'payment' = -
  const [date, setDate] = useState(todayDate());
  const [reason, setReason] = useState('');

  const currentBalance = Number(employee.balance_owed || 0);

  // Compute the signed adjustment that will be applied
  let signed = 0;
  if (mode === 'target') {
    const t = parseFloat(targetBalance);
    if (Number.isFinite(t)) signed = +(t - currentBalance).toFixed(2);
  } else {
    const a = parseFloat(amount || 0);
    if (Number.isFinite(a) && a > 0) signed = direction === 'payment' ? -a : a;
  }
  const newBalance = +(currentBalance + signed).toFixed(2);
  const hasValidInput = mode === 'target'
    ? targetBalance !== '' && Number.isFinite(parseFloat(targetBalance))
    : amount !== '' && parseFloat(amount) > 0;
  const isNoop = Math.abs(signed) < 0.005;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background rounded-lg shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="adjustment-modal">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold flex items-center gap-2">
            <Edit3 className="w-4 h-4" /> Adjust balance — {employee.employee_name}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-4">
          {/* Current balance + live preview header */}
          <div className="grid grid-cols-2 gap-2 rounded-lg border p-3 bg-muted/30" data-testid="adjust-preview">
            <div>
              <div className="text-[10px] uppercase text-muted-foreground">Current balance</div>
              <div className={`text-lg font-bold font-mono ${currentBalance > 0 ? 'text-red-600' : currentBalance < 0 ? 'text-emerald-600' : ''}`}>
                {currentBalance > 0 ? '+' : currentBalance < 0 ? '−' : ''}£{Math.abs(currentBalance).toFixed(2)}
              </div>
              <div className="text-[10px] text-muted-foreground">{currentBalance >= 0 ? 'owed' : 'overpaid'}</div>
            </div>
            <div className={`pl-3 border-l ${!hasValidInput || isNoop ? 'opacity-40' : ''}`}>
              <div className="text-[10px] uppercase text-muted-foreground">New balance</div>
              <div className={`text-lg font-bold font-mono ${newBalance > 0 ? 'text-red-600' : newBalance < 0 ? 'text-emerald-600' : ''}`} data-testid="adjust-new-balance">
                {newBalance > 0 ? '+' : newBalance < 0 ? '−' : ''}£{Math.abs(newBalance).toFixed(2)}
              </div>
              <div className="text-[10px] text-muted-foreground">
                {hasValidInput && !isNoop && (
                  <>adjustment {signed >= 0 ? '+' : '−'}£{Math.abs(signed).toFixed(2)}</>
                )}
              </div>
            </div>
          </div>

          {/* Mode toggle */}
          <div className="flex rounded-md border p-0.5" data-testid="adjust-mode-toggle">
            <button
              type="button"
              onClick={() => setMode('target')}
              className={`flex-1 px-3 py-1.5 text-xs font-medium rounded transition ${mode === 'target' ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="adjust-mode-target"
            >
              Set new balance to…
            </button>
            <button
              type="button"
              onClick={() => setMode('amount')}
              className={`flex-1 px-3 py-1.5 text-xs font-medium rounded transition ${mode === 'amount' ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="adjust-mode-amount"
            >
              Adjustment amount…
            </button>
          </div>

          {mode === 'target' ? (
            <div>
              <Label className="text-xs">New balance owed (£)</Label>
              <Input
                type="number" step="0.01"
                value={targetBalance}
                onChange={(e) => setTargetBalance(e.target.value)}
                placeholder={`e.g. 85.10 — currently £${currentBalance.toFixed(2)}`}
                data-testid="adjust-target-balance"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Enter the figure you want the rolling balance to be after this adjustment.
                Use <code>0</code> to clear it (will show the adjustment in the timeline so you can undo it later).
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-2">
                <button
                  type="button"
                  onClick={() => setDirection('earnings')}
                  className={`p-2.5 rounded border-2 text-sm transition-all text-left ${direction === 'earnings' ? 'border-blue-500 bg-blue-50' : 'border-border'}`}
                  data-testid="adjust-direction-earnings"
                >
                  <div className="font-medium flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5" />
                    Add missing earnings <span className="text-blue-600 font-mono text-xs">(+)</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">Increases balance owed (e.g. hours not in your POS).</div>
                </button>
                <button
                  type="button"
                  onClick={() => setDirection('payment')}
                  className={`p-2.5 rounded border-2 text-sm transition-all text-left ${direction === 'payment' ? 'border-emerald-500 bg-emerald-50' : 'border-border'}`}
                  data-testid="adjust-direction-payment"
                >
                  <div className="font-medium flex items-center gap-2">
                    <Banknote className="w-3.5 h-3.5" />
                    Add missing payment <span className="text-emerald-600 font-mono text-xs">(−)</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">Reduces balance owed (e.g. cash paid but not logged).</div>
                </button>
              </div>
              <div>
                <Label className="text-xs">Amount (£)</Label>
                <Input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 500.00" data-testid="adjust-amount" />
              </div>
            </>
          )}

          <div>
            <Label className="text-xs">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="adjust-date" />
          </div>
          <div>
            <Label className="text-xs">Reason (required)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Owed only for days worked from 11 May onwards" data-testid="adjust-reason" />
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onSave(employee.employee_id, signed, date, reason)}
            disabled={!hasValidInput || isNoop || !reason.trim()}
            data-testid="adjust-save"
          >
            Save adjustment
          </Button>
        </div>
      </div>
    </div>
  );
}


function EmployeeDetailDrawer({ employee_id, onClose, onChanged }) {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adjustModal, setAdjustModal] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/payroll/employees/${employee_id}/ledger`);
      setData(res.data);
    } catch { toast.error('Failed to load ledger'); }
    finally { setLoading(false); }
  }, [api, employee_id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const submitAdjustment = async (emp_id, amount, date, reason) => {
    try {
      await api.post(`/payroll/employees/${emp_id}/adjustment`, { amount, date, reason });
      toast.success('Adjustment saved');
      setAdjustModal(null);
      fetchData();
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save adjustment');
    }
  };

  const deletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await api.delete(`/payroll/payments/${paymentId}`);
      toast.success('Payment deleted');
      fetchData();
      onChanged();
    } catch { toast.error('Delete failed'); }
  };

  const deleteAdjustment = async (adjId) => {
    if (!window.confirm('Delete this adjustment?')) return;
    try {
      await api.delete(`/payroll/adjustments/${adjId}`);
      toast.success('Adjustment deleted');
      fetchData();
      onChanged();
    } catch { toast.error('Delete failed'); }
  };

  const [baselineDialog, setBaselineDialog] = useState(false);
  const setBaseline = async (date) => {
    try {
      const res = await api.post(`/payroll/employees/${employee_id}/baseline`, { date });
      const { balance_before, baseline_offset } = res.data || {};
      toast.success(`Baseline set. £${(balance_before || 0).toFixed(2)} treated as settled.`);
      setBaselineDialog(false);
      fetchData();
      onChanged();
      // suppress unused var lint
      void baseline_offset;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to set baseline');
    }
  };
  const clearBaseline = async () => {
    if (!window.confirm('Remove the baseline reset? Historical balances will be restored.')) return;
    try {
      await api.delete(`/payroll/employees/${employee_id}/baseline`);
      toast.success('Baseline removed');
      fetchData();
      onChanged();
    } catch { toast.error('Failed to remove baseline'); }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative bg-background w-full max-w-2xl h-full overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="employee-drawer"
      >
        <div className="sticky top-0 bg-background z-10 border-b p-4 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg">{data?.employee.name}</h3>
            <p className="text-xs text-muted-foreground">
              {data?.employee.role || 'Staff'} · current rate £{(data?.employee.current_rate || 0).toFixed(2)}/h
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded" data-testid="drawer-close"><X className="w-4 h-4" /></button>
        </div>
        {loading || !data ? (
          <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : (
          <div className="p-4 space-y-5">
            <div className="grid grid-cols-3 gap-3">
              <StatCard label="Earned" value={`£${data.totals.earned.toFixed(2)}`} color="text-blue-600" />
              <StatCard label="Paid" value={`£${data.totals.paid.toFixed(2)}`} color="text-green-600" />
              <StatCard
                label={data.totals.balance_owed >= 0 ? 'Owed (rolling)' : 'Overpaid (rolling)'}
                value={`£${Math.abs(data.totals.balance_owed).toFixed(2)}`}
                color={data.totals.balance_owed > 0 ? 'text-red-600' : 'text-emerald-600'}
              />
            </div>

            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-xs text-muted-foreground">
                {data.employee.baseline_date ? (
                  <span className="text-amber-700">
                    Baseline set as of <strong>{data.employee.baseline_date}</strong> — earlier activity is settled.
                  </span>
                ) : (
                  <span>Cumulative balance across all history. Adjust the balance or set a baseline to start fresh.</span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => setAdjustModal({
                  employee_id, employee_name: data.employee.name,
                  balance_owed: data.totals.balance_owed,
                })} data-testid="drawer-adjust">
                  <Edit3 className="w-3 h-3 mr-1" /> Adjust balance
                </Button>
                {data.employee.baseline_date ? (
                  <Button size="sm" variant="outline" onClick={clearBaseline} data-testid="clear-baseline">
                    <X className="w-3 h-3 mr-1" /> Remove baseline
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setBaselineDialog(true)} data-testid="set-baseline">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Set baseline (zero balance)
                  </Button>
                )}
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-sm mb-2 flex items-center gap-1">
                <History className="w-4 h-4" /> Activity ({data.events?.length || 0})
              </h4>
              <p className="text-xs text-muted-foreground mb-2">Earnings &amp; payments in date order, with running balance owed.</p>
              <div className="space-y-1">
                {!data.events || data.events.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-4 text-center">No activity in this range.</p>
                ) : data.events.map((ev, idx) => {
                  const isEarn = ev.kind === 'earnings';
                  const isPayment = ev.kind === 'payment';
                  const isAdjust = ev.kind === 'adjustment';
                  const isBaseline = isAdjust && ev.is_baseline;
                  const isPreBaseline = ev.is_pre_baseline;
                  const borderColor = isBaseline ? 'border-amber-500 bg-amber-50/60'
                    : isEarn ? 'border-blue-400 bg-blue-50/40'
                    : isPayment ? 'border-green-400 bg-green-50/40'
                    : 'border-purple-400 bg-purple-50/40';
                  const periodPartial = isEarn && ev.has_superseded;
                  return (
                    <div
                      key={`${ev.kind}-${ev.id || idx}`}
                      className={`rounded border-l-4 transition-colors ${borderColor} ${isPreBaseline ? 'opacity-50' : ''}`}
                      data-testid={`event-${idx}`}
                    >
                      {isBaseline && (
                        <div className="px-2 pt-1 text-[10px] uppercase tracking-wider text-amber-700 font-medium" data-testid={`event-baseline-${idx}`}>
                          — Baseline set: balance reset to £0 on {ev.date} —
                        </div>
                      )}
                      <div className="flex items-center gap-3 p-2">
                        <div className="w-20 shrink-0 text-xs text-muted-foreground">{ev.date}</div>
                        <div className="shrink-0">
                          {isEarn ? (
                            <Badge className="text-[10px] bg-blue-100 text-blue-700 hover:bg-blue-100">
                              <Clock className="w-2.5 h-2.5 mr-0.5" /> Hours
                            </Badge>
                          ) : isPayment ? (
                            <Badge className={`text-[10px] ${ev.type === 'bank_payment' ? 'bg-green-100 text-green-700 hover:bg-green-100' : 'bg-amber-100 text-amber-700 hover:bg-amber-100'}`}>
                              <Banknote className="w-2.5 h-2.5 mr-0.5" /> {ev.type === 'bank_payment' ? 'Bank' : (ev.method || 'Cash')}
                            </Badge>
                          ) : (
                            <Badge className="text-[10px] bg-purple-100 text-purple-700 hover:bg-purple-100">
                              <Edit3 className="w-2.5 h-2.5 mr-0.5" /> Adjustment
                            </Badge>
                          )}
                        </div>
                        <div className="flex-1 min-w-0 text-xs">
                          {isEarn ? (
                            <>
                              <div>Uploaded period {ev.period_start} → {ev.period_end}</div>
                              <div className="text-muted-foreground">
                                {periodPartial ? (
                                  <>Covers {ev.covered_from}..{ev.covered_to} · </>
                                ) : null}
                                {ev.hours.toFixed(2)}h across {ev.day_count || ev.days?.length || 0} day{((ev.day_count || ev.days?.length || 0) === 1) ? '' : 's'}
                                {periodPartial && (
                                  <span className="ml-1 text-amber-700" title="Some days in this upload were overwritten by a later upload — most recent wins per day.">
                                    ({ev.superseded_days?.length || 0} day{(ev.superseded_days?.length || 0) === 1 ? '' : 's'} superseded by a later upload)
                                  </span>
                                )}
                              </div>
                            </>
                          ) : isPayment ? (
                            <>
                              <div className="capitalize">{ev.type === 'bank_payment' ? 'Bank payment' : `${ev.method || 'Cash'} payment`}</div>
                              {ev.description && <div className="text-muted-foreground truncate">{ev.description}</div>}
                            </>
                          ) : (
                            <>
                              <div>{ev.amount > 0 ? 'Increased balance owed' : 'Reduced balance owed'}</div>
                              {ev.reason && <div className="text-muted-foreground truncate">{ev.reason}</div>}
                            </>
                          )}
                        </div>
                        <div className="shrink-0 text-right">
                          <div className={`font-mono font-semibold ${isEarn ? 'text-blue-600' : isPayment ? 'text-green-600' : (ev.amount > 0 ? 'text-red-600' : 'text-emerald-600')}`}>
                            {isEarn ? '+' : isPayment ? '−' : (ev.amount >= 0 ? '+' : '−')}£{Math.abs(ev.amount).toFixed(2)}
                          </div>
                          <div className={`text-[10px] font-mono ${ev.balance_after > 0 ? 'text-red-500' : ev.balance_after < 0 ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                            bal £{ev.balance_after.toFixed(2)}
                          </div>
                        </div>
                        {(isPayment && ev.type === 'manual_payment') && (
                          <button
                            onClick={() => deletePayment(ev.id)}
                            className="text-muted-foreground hover:text-red-600 ml-1"
                            title="Delete payment"
                            data-testid={`delete-payment-${ev.id}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                        {isAdjust && (
                          <button
                            onClick={() => deleteAdjustment(ev.id)}
                            className="text-muted-foreground hover:text-red-600 ml-1"
                            title="Delete adjustment"
                            data-testid={`delete-adjustment-${ev.id}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      {isEarn && Array.isArray(ev.days) && ev.days.length > 0 && (
                        <details className="px-2 pb-2" data-testid={`event-days-${idx}`}>
                          <summary className="text-[11px] text-muted-foreground cursor-pointer hover:text-foreground ml-[96px]">
                            Show day-by-day breakdown ({ev.days.length})
                          </summary>
                          <div className="mt-1 ml-[96px] grid grid-cols-1 gap-0.5">
                            {ev.days.map((d) => (
                              <div key={d.date} className="flex items-center justify-between text-[11px] font-mono">
                                <span className="text-muted-foreground">{d.date}</span>
                                <span>{d.hours.toFixed(2)}h × £{d.rate.toFixed(2)}</span>
                                <span className="text-blue-600">£{d.earned.toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {data.employee.rate_history && data.employee.rate_history.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-1">
                  <History className="w-4 h-4" /> Rate history
                </h4>
                <div className="space-y-1">
                  {[...data.employee.rate_history].reverse().map((h, i) => (
                    <div key={i} className="flex items-center justify-between text-sm bg-muted/30 rounded p-2">
                      <div className="text-xs">
                        {h.effective_from} {h.effective_to ? <>→ {h.effective_to}</> : <span className="text-emerald-600 font-medium">→ current</span>}
                        {h.note && <span className="text-muted-foreground ml-2">· {h.note}</span>}
                      </div>
                      <div className="font-mono">£{(h.amount || 0).toFixed(2)}/h</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {baselineDialog && data && (
          <BaselineDialog
            current_balance={data.totals.balance_owed}
            onClose={() => setBaselineDialog(false)}
            onSave={setBaseline}
          />
        )}
        {adjustModal && (
          <AdjustmentModal
            employee={adjustModal}
            onClose={() => setAdjustModal(null)}
            onSave={submitAdjustment}
          />
        )}
      </div>
    </div>
  );
}

function BaselineDialog({ current_balance, onClose, onSave }) {
  const [date, setDate] = useState(todayDate());
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="baseline-dialog">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Set baseline (zero balance)
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs">
            <p className="font-medium text-blue-900">What this does</p>
            <p className="text-blue-800 mt-1">
              Treat everything before the chosen date as <strong>settled</strong>. The cumulative balance becomes £0.00 on that date. Earnings and payments before then will still be visible in the timeline (greyed out) but won&apos;t affect the rolling balance.
            </p>
            <p className="text-blue-800 mt-2">
              Current rolling balance: <strong>£{Math.abs(current_balance).toFixed(2)}</strong> {current_balance >= 0 ? 'owed' : 'overpaid'}
            </p>
          </div>
          <div>
            <Label className="text-xs">Baseline date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="baseline-date" />
            <p className="text-[11px] text-muted-foreground mt-1">
              Typically the start of the month you began using Tillytics to track payments.
            </p>
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(date)} disabled={!date} data-testid="baseline-save">
            Set baseline
          </Button>
        </div>
      </div>
    </div>
  );
}
